import React, { useState, useEffect } from 'react';
import { COUNTRIES, COUNTRY_DIAL_CODES } from '../data/countries';

export default function EnquiryForm({
  theme = 'light',
  title,
  description,
  onSubmit,
  submitButtonText = 'Send',
}) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user's country based on IP geolocation
  useEffect(() => {
    const fetchUserCountry = async () => {
      try {
        const response = await fetch('https://geolocation-db.com/json/geoip');
        const data = await response.json();
        if (data.country_name && COUNTRIES.includes(data.country_name)) {
          setFormData((prev) => ({ ...prev, country: data.country_name }));
        }
      } catch (error) {
        console.error('Error fetching country:', error);
        // Silently fail - user can manually select country
      }
    };

    fetchUserCountry();
  }, []);

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'first_name':
        if (!value.trim()) {
          newErrors.first_name = 'First name is required';
        } else if (value.trim().length < 2) {
          newErrors.first_name = 'First name must be at least 2 characters';
        } else {
          delete newErrors.first_name;
        }
        break;

      case 'last_name':
        if (!value.trim()) {
          newErrors.last_name = 'Last name is required';
        } else if (value.trim().length < 2) {
          newErrors.last_name = 'Last name must be at least 2 characters';
        } else {
          delete newErrors.last_name;
        }
        break;

      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;

      case 'message':
        if (!value.trim()) {
          newErrors.message = 'Message is required';
        } else if (value.trim().length < 10) {
          newErrors.message = 'Message must be at least 10 characters';
        } else {
          delete newErrors.message;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    } else if (formData.first_name.trim().length < 2) {
      newErrors.first_name = 'First name must be at least 2 characters';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    } else if (formData.last_name.trim().length < 2) {
      newErrors.last_name = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    setTouched({
      first_name: true,
      last_name: true,
      email: true,
      phone: true,
      country: true,
      message: true,
    });

    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        // Get reCAPTCHA token
        const token = await window.grecaptcha.enterprise.execute('6LdyW8MsAAAAAAS92_pPSqew_1vqdj1s4RbrfnkW', { action: 'submit' });
        
        const submittedPhone = phonePrefix
          ? formData.phone.startsWith(phonePrefix)
            ? formData.phone
            : `${phonePrefix}${formData.phone}`
          : formData.phone;

        const formPayload = {
          ...formData,
          phone: submittedPhone,
        };

        // Send to backend for verification
        const response = await fetch('/api/verify-captcha', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            formData: formPayload,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Form submission failed');
        }

        // Success - reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          country: '',
          message: '',
        });
        setErrors({});
        setTouched({});

        // Trigger callback with success
        if (onSubmit) {
          onSubmit(formPayload);
        }

        // Show success message (optional)
        alert('Thank you! Your enquiry has been submitted successfully.');

      } catch (error) {
        console.error('Form submission error:', error);
        setErrors({ submit: error.message || 'An error occurred. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isDark = theme === 'dark';
  const phonePrefix = formData.country ? COUNTRY_DIAL_CODES[formData.country] || '' : '';
  const phonePrefixTextClasses = 'text-[#4ccdcf]';

  // Theme-specific classes
  const formClasses = isDark
    ? 'rounded-none bg-white/10 p-8 shadow-soft backdrop-blur-sm'
    : 'rounded-none bg-white p-8 shadow-sm';

  const labelClasses = isDark
    ? 'flex flex-col gap-2 text-sm font-medium'
    : 'flex flex-col gap-2 text-sm font-medium text-neutral';

  const getInputClasses = (fieldName) => {
    const baseClasses = isDark
      ? 'w-full rounded-none border-2 bg-white px-3 py-2 text-sm text-[#4ccdcf] placeholder:text-white/50 focus:outline-none focus:ring-2 hover:bg-white/70'
      : 'w-full rounded-none border-2 bg-white px-3 py-2 text-sm text-[#4ccdcf] focus:outline-none focus:ring-2';

    const hasError = errors[fieldName] && touched[fieldName];
    const errorClasses = isDark
      ? 'border-red-400 focus:border-red-400 focus:ring-red-300'
      : 'border-red-400 focus:border-red-400 focus:ring-red-300';
    const validClasses = isDark
      ? 'border-white/30 focus:border-white focus:ring-white/30'
      : 'border-neutral/30 focus:border-brand focus:ring-brand/30';

    return `${baseClasses} ${hasError ? errorClasses : validClasses}`;
  };

  const getInputStyle = (fieldName) => {
    if (fieldName !== 'phone' || !phonePrefix) {
      return undefined;
    }

    const paddingBasedOnPrefix = phonePrefix.length * 12 + 10;
    return { paddingLeft: `${paddingBasedOnPrefix}px` };
  };

  const getTextareaClasses = (fieldName) => {
    const baseClasses = isDark
      ? 'h-28 rounded-none border-2 bg-white px-3 py-2 text-sm text-[#4ccdcf] placeholder:text-white/50 focus:outline-none focus:ring-2 hover:bg-white/70'
      : 'h-28 rounded-none border-2 bg-white px-3 py-2 text-sm text-[#4ccdcf] focus:outline-none focus:ring-2';

    const hasError = errors[fieldName] && touched[fieldName];
    const errorClasses = isDark
      ? 'border-red-400 focus:border-red-400 focus:ring-red-300'
      : 'border-red-400 focus:border-red-400 focus:ring-red-300';
    const validClasses = isDark
      ? 'border-white/30 focus:border-white focus:ring-white/30'
      : 'border-neutral/30 focus:border-brand focus:ring-brand/30';

    return `${baseClasses} ${hasError ? errorClasses : validClasses}`;
  };

  const requiredSpanClasses = isDark ? 'text-white/70' : 'text-neutral/60';
  const errorMessageClasses = isDark ? 'text-red-500 text-xs mt-1' : 'text-red-500 text-xs mt-1';

  return (
    <>
      {(title || description) && (
        <div className={isDark ? 'space-y-4 mb-8' : 'space-y-6 mb-8'}>
          {title && (
            <h2 className={isDark ? 'text-3xl font-semibold text-white' : 'text-3xl font-semibold text-brand'}>
              {title}
            </h2>
          )}
          {description && (
            <p className={isDark ? 'max-w-lg text-white/85' : 'text-neutral/70'}>
              {description}
            </p>
          )}
        </div>
      )}

      <form className={`${formClasses} grid gap-6`} onSubmit={handleFormSubmit} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClasses}>
              <div className="flex items-center gap-1">
                <span>First Name</span>
                <span className={requiredSpanClasses}>(required)</span>
              </div>
              <input
                className={getInputClasses('first_name')}
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </label>
            {errors.first_name && touched.first_name && (
              <p className={errorMessageClasses}>{errors.first_name}</p>
            )}
          </div>
          <div>
            <label className={labelClasses}>
              <div className="flex items-center gap-1">
                <span>Last Name</span>
                <span className={requiredSpanClasses}>(required)</span>
              </div>
              <input
                className={getInputClasses('last_name')}
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </label>
            {errors.last_name && touched.last_name && (
              <p className={errorMessageClasses}>{errors.last_name}</p>
            )}
          </div>
        </div>

        <div>
          <label className={labelClasses}>
            <div className="flex items-center gap-1">
              <span>Email</span>
              <span className={requiredSpanClasses}>(required)</span>
            </div>
            <input
              className={getInputClasses('email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </label>
          {errors.email && touched.email && (
            <p className={errorMessageClasses}>{errors.email}</p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClasses}>
            <span>Country</span>
            <select
              className={getInputClasses('country')}
              name="country"
              value={formData.country}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {formData.country === '' && <option value=""></option>}
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClasses}>
            <span>Phone</span>
            <div className="relative">
              {phonePrefix && (
                <span className={`pointer-events-none absolute inset-y-0 left-0 flex items-center px-3 text-sm ${phonePrefixTextClasses}`}>
                  {phonePrefix}
                </span>
              )}
              <input
                className={getInputClasses('phone')}
                style={getInputStyle('phone')}
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </label>
        </div>

        <div>
          <label className={labelClasses}>
            <div className="flex items-center gap-1">
              <span>Message</span>
              <span className={requiredSpanClasses}>(required)</span>
            </div>
            <textarea
              className={getTextareaClasses('message')}
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </label>
          {errors.message && touched.message && (
            <p className={errorMessageClasses}>{errors.message}</p>
          )}
        </div>

        {errors.submit && (
          <p className={errorMessageClasses}>{errors.submit}</p>
        )}

        <button 
          type="submit" 
          disabled={isSubmitting} 
          className="btn-primary w-full bg-white text-[#4ccdcf] hover:bg-white/70 font-normal uppercase disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Sending...' : 'Send Enquiry'}
        </button>
      </form>
    </>
  );
}
